/**
 * Gets a new clone of given reference quaternion.
 * @param {quaternion} $quaternion The reference quaternion.
 * @returns {quaternion}
 */
function clone($quaternion) {

    return [...$quaternion];
}

/**
 * Creates a new 4x4 quaternion.
 * @param {[number, number, number, number]} $values The matrix values.
 * @returns {quaternion}
 */
function create($values) {

    return [...$values];
}

/**
 * Check the equality of two given quaternions.
 * @param {quaternion} $quaterniona The quaternion to check on.
 * @param {quaternion} $quaternionb The quaternion to check with.
 * @returns {boolean}
 */
 function equal($quaterniona, $quaternionb) {

    const [xa, ya, za, wa] = $quaterniona;
    const [xb, yb, zb, wb] = $quaternionb;

    return xa === xb
    && ya === yb
    && za === zb
    && wa === wb;
}

/**
 * Gets a new quaternion from given reference Euler angles.
 * @param {[number, number, number]} $angles The reference Euler angles in radians for the x-axis, y-axis and z-axis.
 * @returns {quaternion}
 */
function fromeuler($angles) {

    let [x, y, z] = $angles;

    x *= 0.5;
    y *= 0.5;
    z *= 0.5;

    const quaternion = identity();

    const cosinex = Math.cos(x);
    const sinex = Math.sin(x);

    const cosiney = Math.cos(y);
    const siney = Math.sin(y);

    const cosinez = Math.cos(z);
    const sinez = Math.sin(z);

    quaternion[0] = sinex * cosiney * cosinez - cosinex * siney * sinez;
    quaternion[1] = cosinex * siney * cosinez + sinex * cosiney * sinez;
    quaternion[2] = cosinex * cosiney * sinez - sinex * siney * cosinez;
    quaternion[3] = cosinex * cosiney * cosinez + sinex * siney * sinez;

    return quaternion;
}

/**
 * Gets a new identity quaternion.
 * @returns {quaternion}
 */
function identity() {

    return [0, 0, 0, 1];
}

/**
 * Gets a new inverted quaternion from given quaternion.
 * @param {quaternion} $quaternion The reference quaternion.
 * @returns {quaternion}
 */
function invert($quaternion) {

    const [x, y, z, w] = $quaternion;

    const quaternion = identity();

    const dot = x * x + y * y + z * z + w * w;

    if (dot === 0) {

        quaternion[0] = 0;
        quaternion[1] = 0;
        quaternion[2] = 0;
        quaternion[3] = 0;

        return quaternion;
    }

    const dotinverse = 1 / dot;

    quaternion[0] = - x * dotinverse;
    quaternion[1] = - y * dotinverse;
    quaternion[2] = - z * dotinverse;
    quaternion[3] = w * dotinverse;

    return quaternion;
}

/**
 * Gets a new multiplied quaternion from two given quaternions.
 * @param {quaternion} $quaterniona The quaternion to multiply on.
 * @param {quaternion} $quaternionb The quaternion to multiply with.
 * @returns {quaternion}
 */
function multiply($quaterniona, $quaternionb) {

    const [ax, ay, az, aw] = $quaterniona;
    const [bx, by, bz, bw] = $quaternionb;

    const quaternion = identity();

    quaternion[0] = ax * bw + aw * bx + ay * bz - az * by;
    quaternion[1] = ay * bw + aw * by + az * bx - ax * bz;
    quaternion[2] = az * bw + aw * bz + ax * by - ay * bx;
    quaternion[3] = aw * bw - ax * bx - ay * by - az * bz;

    return quaternion;
}

/**
 * Gets a new normalize quaternion from given reference quaternion.
 * @param {quaternion} $quaternion The reference quaternion.
 * @returns {quaternion}
 */
function normalize($quaternion) {

    const [x, y, z, w] = $quaternion;

    const quaternion = identity();

    let length = x * x + y * y + z * z + w * w;

    if (length > 0) {

        length = 1 / Math.sqrt(length);
    }

    quaternion[0] = x * length;
    quaternion[1] = y * length;
    quaternion[2] = z * length;
    quaternion[3] = w * length;

    return quaternion;
}

// @TODO
function toeuler($quaternion) {
    const [x, y, z, w] = $quaternion;

    const ysqr = y * y;

    const t0 = 2.0 * (w * x + y * z);
    const t1 = 1.0 - 2.0 * (x * x + ysqr);
    const roll = Math.atan2(t0, t1);

    const t2 = 2.0 * (w * y - z * x);
    t2 > 1.0 ? 1.0 : t2;
    t2 < -1.0 ? -1.0 : t2;
    const pitch = Math.asin(t2);

    const t3 = 2.0 * (w * z + x * y);
    const t4 = 1.0 - 2.0 * (ysqr + z * z);
    const yaw = Math.atan2(t3, t4);

    return [roll, pitch, yaw];
  }

export {

    clone,
    create,
    equal,
    fromeuler,
    identity,
    invert,
    multiply,
    normalize,
    toeuler
};
